import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useTranslations } from "next-intl";
import { ContentViewBlock, ContentViewBlockProps } from "@components/common/content/content-view-block";
import { Badge } from "@components/ui/badge";
import { proposalsTools } from "@features/proposals/proposals-tools";
import { ActivityMemberEntity, ActivityMemberInputView } from "@features/proposals/types/member";
import { MemberPublicationConsentEditDialog } from "@features/proposals/ui/member/member-publication-consent-edit-dialog";
interface MemberPublicationConsentStatusProps extends Omit<ContentViewBlockProps, "title"> {
  member: ActivityMemberEntity;
  patchMember?: (member: ActivityMemberEntity, body: Partial<ActivityMemberInputView>) => Promise<undefined | ActivityMemberEntity>;
  className?: string;
}

/**
 * Component for displaying and editing activity member publication consent status.
 * Shows whether the member has accepted the consent for content publishing.
 * Allows editing based on user permissions:
 * - If isMe=true: can only accept/decline consent
 * - If isMe=false and canPatch=true: can set third-party consent with reason
 */
export const MemberPublicationConsentStatus = t0 => {
  const $ = _c(60);
  let actions;
  let member;
  let patchMember;
  let props;
  if ($[0] !== t0) {
    ({
      member,
      actions,
      patchMember,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = actions;
    $[2] = member;
    $[3] = patchMember;
    $[4] = props;
  } else {
    actions = $[1];
    member = $[2];
    patchMember = $[3];
    props = $[4];
  }
  const t = useTranslations("features.proposals.ui.memberPublicationConsentStatus");
  let t1;
  if ($[5] !== member) {
    t1 = proposalsTools.activityMemberTools.isMemberPublicationConsentAccepted(member);
    $[5] = member;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const isAccepted = t1;
  let T0;
  let consentForm;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== actions || $[8] !== isAccepted || $[9] !== member || $[10] !== patchMember || $[11] !== props || $[12] !== t) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      consentForm = proposalsTools.activityMemberTools.getPublicationConsentForm(member?.eventProfile?.consent);
      const canPatch = member?.rights?.canPatch;
      const isMe = member?.isMe ?? true;
      if (isAccepted === null) {
        t9 = null;
        break bb0;
      }
      const editDialog = canPatch && patchMember ? <MemberPublicationConsentEditDialog member={member} isMe={isMe} consentForm={consentForm} patchMember={patchMember} /> : null;
      T0 = ContentViewBlock;
      if ($[23] !== t) {
        t5 = t("title");
        $[23] = t;
        $[24] = t5;
      } else {
        t5 = $[24];
      }
      if ($[25] !== t) {
        t6 = t("description");
        $[25] = t;
        $[26] = t6;
      } else {
        t6 = $[26];
      }
      t7 = editDialog ?? actions;
      t8 = props;
      t2 = "flex flex-col gap-2";
      let t10;
      if ($[27] !== t) {
        t10 = t("status.label");
        $[27] = t;
        $[28] = t10;
      } else {
        t10 = $[28];
      }
      let t11;
      if ($[29] !== t10) {
        t11 = <span className="text-sm font-medium">{t10}:</span>;
        $[29] = t10;
        $[30] = t11;
      } else {
        t11 = $[30];
      }
      const t12 = isAccepted ? "default" : "outline";
      let t13;
      if ($[31] !== isAccepted || $[32] !== t) {
        t13 = isAccepted === true ? t("status.accepted") : isAccepted === false ? t("status.revoked") : t("status.notAccepted");
        $[31] = isAccepted;
        $[32] = t;
        $[33] = t13;
      } else {
        t13 = $[33];
      }
      let t14;
      if ($[34] !== t12 || $[35] !== t13) {
        t14 = <Badge variant={t12} className="ml-auto">{t13}</Badge>;
        $[34] = t12;
        $[35] = t13;
        $[36] = t14;
      } else {
        t14 = $[36];
      }
      if ($[37] !== t11 || $[38] !== t14) {
        t3 = <div className="flex items-center gap-2">{t11}{t14}</div>;
        $[37] = t11;
        $[38] = t14;
        $[39] = t3;
      } else {
        t3 = $[39];
      }
      t4 = consentForm?.acceptedAt && <div className="text-xs text-muted-foreground">{t("acceptedAt")}: {new Date(consentForm.acceptedAt).toLocaleDateString()}</div>;
    }
    $[7] = actions;
    $[8] = isAccepted;
    $[9] = member;
    $[10] = patchMember;
    $[11] = props;
    $[12] = t;
    $[13] = T0;
    $[14] = consentForm;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    T0 = $[13];
    consentForm = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[40] !== consentForm || $[41] !== t) {
    t10 = consentForm?.acceptedByName && <div className="text-xs text-muted-foreground">{t("acceptedBy")}: {consentForm.acceptedByName}</div>;
    $[40] = consentForm;
    $[41] = t;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== consentForm || $[44] !== isAccepted || $[45] !== t) {
    t11 = consentForm?.acceptedByThirdParty && consentForm?.acceptedByThirdPartyReason && <div className="text-xs text-muted-foreground">{isAccepted ? t("thirdPartyReason.give") : t("thirdPartyReason.revoke")}:{" "}{consentForm.acceptedByThirdPartyReason}</div>;
    $[43] = consentForm;
    $[44] = isAccepted;
    $[45] = t;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t2 || $[50] !== t3 || $[51] !== t4) {
    t12 = <div className={t2}>{t3}{t4}{t10}{t11}</div>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t2;
    $[50] = t3;
    $[51] = t4;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== T0 || $[54] !== t12 || $[55] !== t5 || $[56] !== t6 || $[57] !== t7 || $[58] !== t8) {
    t13 = <T0 title={t5} description={t6} actions={t7} {...t8}>{t12}</T0>;
    $[53] = T0;
    $[54] = t12;
    $[55] = t5;
    $[56] = t6;
    $[57] = t7;
    $[58] = t8;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  return t13;
};